import type { Ref } from 'vue';
import { defineComponent, getCurrentInstance, nextTick, ref } from 'vue';
import { Image, Popup, Toast } from '@cfx/base-mobile';
import { OpenData } from '@dustess/cf-design-biz-mobile';
import UserApi from '@api/mk-material-center/services/api/UserApi';
import CardApi from '@api/card/services/api/CardApi';
import styles from './index.module.scss';
import { loadMountDocument } from '@cfx/icorki-jwx';

export default defineComponent({
  name: 'MkLinkMe',
  props: {
    isIframe: {
      type: Boolean,
      default: false
    },
    marginBottom: {
      type: Number,
      default: 8
    }
  },
  setup(props) {
    const show = ref(false);

    const globalProperties = getCurrentInstance()?.appContext?.config?.globalProperties as Record<string, any>;
    const contactMeDetail: Ref<any> = ref({});

    const handleData = (data: any) => {
      contactMeDetail.value = data;

      if (!contactMeDetail.value.qwCodeURL) {
        // 必须要有地址 组件中才会显示一个破损图标
        contactMeDetail.value.qwCodeURL = 'qwCodeURL';
      }
    };

    // 获取销售信息
    const getUserContactMeDetail = async () => {
      const urlObj = globalProperties.$urlObj;
      const params = {
        type: '1',
        userId: urlObj.userid || '',
        qwUserId: urlObj.qwUserId || '',
        materialId: globalProperties.$materialId,
        corpId: urlObj.corpId || '',
        cid: urlObj.account || ''
      };
      const { code, success, data }: any = await UserApi.v1UserContactMeDetailGet(params);
      if (code === 500200 && success && data) handleData(data);
    };

    // 获取全部信息
    const allInfo = ref<any>({});
    const getUserAllInfo = async () => {
      const urlObj = globalProperties.$urlObj;
      const params = {
        type: '2',
        userId: urlObj.userid || '',
        qwUserId: urlObj.qwUserId || '',
        materialId: globalProperties.$materialId,
        corpId: urlObj.corpId || '',
        cid: urlObj.account || ''
      };
      const { code, success, data }: any = await UserApi.v1UserContactMeDetailGet(params);
      if (code === 500200 && success && data) allInfo.value = data;
    };

    // 获取当前销售名片信息, 主要用于判断名片icon是否显示
    const cardInfo: Ref<any> = ref({});
    const getCardInfo = async (params: {
      companyId: string
      userId: string
    }) => {
      const { success, data } = await CardApi.cardUserCardGet(params);
      if (success && data) cardInfo.value = data;
    };

    // 进入名片事件-调用小程序原生的方法
    const enterCardFun = async () => {
      if (globalProperties.$isView) {
        Toast('当前为预览状态');
        return;
      }

      // IOS用户在企微中不支持从H5跳转至小程序
      if (globalProperties.$isQwMp && globalProperties.$isIOS) {
        Toast('IOS企微环境不支持');
        return;
      }

      try {
        await loadMountDocument(['//res.wx.qq.com/open/js/jweixin-1.3.2.js']);
        console.log('wx', window.wx);
        if (window.wx && window.wx.miniProgram) {
          window.wx.miniProgram.reLaunch({ url: `/pages/person/index?scene=${cardInfo.value.sceneId}` });
        }
      }
      catch (err) {
        console.error(err);
      }
    };

    // 拨打电话
    const handleToCall = () => {
      if (globalProperties.$isView) {
        Toast('当前为预览状态');
        return;
      }

      if (!globalProperties.$isMobile) {
        Toast('请在移动端中打开');
        return;
      }
      window.location.href = `tel:${allInfo.value.phone}`;

      globalProperties.$report('mkFullLinkClick', { actionType: 'phone' });
    };

    if (globalProperties.$urlObj.iframeData === 'yes' || props.isIframe) {
      window.parent.postMessage({ postType: 'iframe:ready' }, '*');
      window.addEventListener(
        'message',
        (e) => {
          const { data } = e;

          if (data.postType) {
            // 当data.postType存在时，表明父窗口正确传值过来了
            if (data.postType === 'link-me') {
              const info = data.postData || {};
              // 传递的是事件
              handleData(info);
              allInfo.value = info;
              cardInfo.value = { cardStatus: true };
              nextTick(() => {
                if (props.isIframe) show.value = true;
              });
            }
          }
        },
        false
      );
    }
    else {
      const urlObj = globalProperties.$urlObj;
      getUserAllInfo();
      if (urlObj.qwUid) {
        getCardInfo({
          companyId: urlObj.account || '',
          userId: urlObj.qwUid || ''
        });
      }
      getUserContactMeDetail();
    }

    const handleShow = () => {
      if (globalProperties.$urlObj.iframeData === 'yes') {
        Toast('当前为预览状态');
        return;
      }
      show.value = !show.value;

      globalProperties.$report('mkFullLinkClick', { actionType: 'wechat' });
    };

    // 显示open-data
    const setShowOpenData = (name: string) => (globalProperties.$isThirdPartyApp ? <OpenData isTripartite={globalProperties.$isThirdPartyApp} name={name} /> : name);
    // 处理简介
    const setShowDesc = (desc: string) => {
      // eslint-disable-next-line prefer-regex-literals
      const reg = new RegExp('\\$userName\\=.*?(\\$)|\\$departmentName\\=.*?(\\$)', 'g');
      // desc = '设计$userName=sjklj$了多$departmentName=sfjll$款健身房';
      const resArr = desc?.match(reg);
      if (!resArr) return desc;
      desc = desc?.replace(reg, ss => `*@*@*${ss}*@*@*`);
      return desc?.split('*@*@*').map(item => (item.match(reg) ? setShowOpenData(item) : item));
    };

    globalProperties.$wxSdk().init();

    return () => (
      <>
        {!props.isIframe
          ? <div class={`${styles['mk-link-me']} mk-bg-white mk-split-line-b flex-s`} style={{ marginBottom: `${props.marginBottom}px` }}>
            {allInfo.value.avatar ? <Image class={styles['staff-head']} round src={allInfo.value.avatar} /> : ''}
            <div class={`${styles['staff-info']} flex-1`}>
              <p class={'mk-fs-14 mk-fw-500 mk-text text-ellipsis-1'}>{setShowOpenData(allInfo.value.name)}</p>
              <p class={`${styles['staff-company']} text-ellipsis-1 mk-text-sub-2 mk-fs-12`}>{setShowOpenData(allInfo.value.companyName || allInfo.value.corpName)}</p>
            </div>

            <div class={`${styles['staff-right']} flex-s`}>
              {(cardInfo.value.cardStatus && globalProperties.$sourceCardMini) ? <i onClick={enterCardFun}><duero-iconfont type="symbol" name="duero-icon-3181568-mingpian" /></i> : ''}
              {allInfo.value.isShowWx ? <i onClick={handleShow}><duero-iconfont iconClass={styles.icon} type="symbol" name="duero-icon-3181568-weixin11" /></i> : ''}
              {allInfo.value.phone ? <i onClick={handleToCall}><duero-iconfont iconClass={styles.icon} type="symbol" name="duero-icon-3181568-bodadianhua" /></i> : ''}
            </div>
          </div>
          : ''}
        <Popup
          round
          close-on-click-overlay={!props.isIframe}
          style="width: calc(100% - 56px); max-width: 320px; border-radius: 8px;"
          v-model={[show.value, 'show']}
          teleport="body"
        >
          <div class={`${styles['popup-content']} flex-c`}>
            {contactMeDetail.value.defaultBgImg ? <Image class={styles['popup-content__bg']} fit="cover" src={contactMeDetail.value.defaultBgImg} /> : ''}
            {(contactMeDetail.value.avatar || contactMeDetail.value.name || contactMeDetail.value.companyName || contactMeDetail.value.phone)
              ? <div class={`${styles['popup-content__user']} flex-s`}>
                {contactMeDetail.value.avatar ? <Image class={styles.head} round src={contactMeDetail.value.avatar} /> : ''}
                <div class={`${styles.info} flex-1`}>
                  {contactMeDetail.value.name ? <p class={'mk-fs-18 mk-fw-500 text-ellipsis-1'}>{setShowOpenData(contactMeDetail.value.name)}</p> : ''}
                  {contactMeDetail.value.companyName ? <p class={`${styles.company} text-ellipsis-1 mk-fs-13 mk-text-sub`}>{setShowOpenData(contactMeDetail.value.companyName)}</p> : ''}
                  {contactMeDetail.value.phone ? <p class={`${styles.phone} text-ellipsis-1 mk-fs-13 mk-text-sub`}>{`联系电话：${contactMeDetail.value.phone}`}</p> : ''}
                </div>
              </div>
              : ''}
            <p class={`${styles['popup-content__tips']} text-ellipsis-3 mk-fs-13 mk-text-sub`}>{setShowDesc(contactMeDetail.value.desc)}</p>
            <>
              <Image class={styles['popup-content__qrcode']} src={contactMeDetail.value.qwCodeURL} />

              <p class="mk-fs-12 mk-text-sub-2">{contactMeDetail.value.qwCodeURL === 'qwCodeURL' ? '二维码加载失败' : '扫一扫上面的二维码图案，加我微信'}</p>
            </>
          </div>
        </Popup>
      </>
    );
  }
});
